import React from "react";
import { DownOutlined } from "@ant-design/icons";

import { formatDay, isFollowUp, outcomeChipStyle, outcomeLabel } from "./outcomes";

/**
 * The status cell.
 *
 * Never the internal open/fulfilled/cancelled. A Ready request says what came
 * back — the filter it sits under (outcomes.js): "Covered", "Paid", "Details
 * Needed by Smilepass", or the office's own "Contact patient" with the answer beneath
 * it. A next check our team scheduled ("Queued for follow-up") says
 * when. A verified breakdown, which has no answer to name, just reads "Ready".
 *
 * "In progress" stays reassuring: an agent that hit a 2FA wall or a dead portal
 * is still in progress from the office's point of view — our team simply
 * finishes it — and a scheduled check says when ("Payment being processed ·
 * next check Oct 3"). The office is never shown our plumbing.
 *
 * `caret` marks the cell as the office's status picker (RequestsTable wraps it
 * in the menu); the chip itself stays display-only.
 */

export const STATUS_LABEL = {
  ready: "Ready",
  in_progress: "In progress",
  archived: "Archived",
  cancelled: "Withdrawn",
};

const SUBTEXT = {
  // Shown only when an agent tried and handed over — reassurance, not an error.
  handover: "Our team is completing this",
  live: "Checking with the carrier",
  terminated: "Plan not active",
};

export default function RequestStatusChip({
  status, readyKey, answer, isLive, fellBack, isTerminatedPlan, followUp, caret,
}) {
  let label = STATUS_LABEL[status] || status;
  let tint = null;
  let sub = null;

  if (status === "ready" && readyKey) {
    label = outcomeLabel(readyKey);
    tint = outcomeChipStyle(readyKey);
    const nextCheck = isFollowUp(answer) && followUp && followUp.date
      ? "next check " + formatDay(followUp.date, false) : null;
    // The office's own status sits over the answer it is acting on.
    if (answer && answer !== readyKey) sub = outcomeLabel(answer) + (nextCheck ? " · " + nextCheck : "");
    else if (nextCheck) sub = nextCheck.charAt(0).toUpperCase() + nextCheck.slice(1);
  } else if (status === "ready" && isTerminatedPlan) {
    sub = SUBTEXT.terminated;
  } else if (status === "in_progress") {
    if (isLive) sub = SUBTEXT.live;
    else if (followUp && followUp.date) {
      sub = outcomeLabel(followUp.outcome || "follow_up_again") + " · next check " + formatDay(followUp.date, false);
    } else if (fellBack) sub = SUBTEXT.handover;
  } else if (status === "archived" && readyKey) {
    sub = outcomeLabel(readyKey);
  }

  const className = [
    "ir-status",
    `ir-status-${status}`,
    isLive && status === "in_progress" ? "ir-status-live" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div>
      <span className={className} style={tint ? { color: tint.color } : undefined}>
        <span className="ir-status-dot" />
        <span className="ir-status-label">
          {label}
          {caret ? <DownOutlined className="ir-status-caret" /> : null}
        </span>
      </span>
      {sub ? <span className="ir-status-sub">{sub}</span> : null}
    </div>
  );
}
